import { createContext, useContext, useState, ReactNode, useCallback, useMemo } from 'react';

export interface ActiveAccessoryInfo {
  id: string;
  name: string;
  category: string;
  // Port specs for AI to understand capacity
  powerOutputs?: string[];
  powerInputs?: string[];
  videoOutputs?: string[];
  videoInputs?: string[];
}

interface RigContextState {
  cameraId?: string;
  cameraName?: string;
  rigStyle?: string;
  selectedAccessories?: string[];
  // Active rig info for real-time AI operations
  activeRigId?: string;
  activeProjectId?: string;
  activeAccessories?: ActiveAccessoryInfo[];
}

interface RigContextValue {
  context: RigContextState;
  setContext: (context: RigContextState) => void;
  clearContext: () => void;
}

const RigContext = createContext<RigContextValue | null>(null);

export function RigContextProvider({ children }: { children: ReactNode }) {
  const [context, setContextState] = useState<RigContextState>({});

  const setContext = useCallback((newContext: RigContextState) => {
    setContextState(newContext);
  }, []);

  const clearContext = useCallback(() => {
    setContextState({});
  }, []);

  const value = useMemo(() => ({ context, setContext, clearContext }), [context, setContext, clearContext]);

  return (
    <RigContext.Provider value={value}>
      {children}
    </RigContext.Provider>
  );
}

export function useRigContext() {
  const ctx = useContext(RigContext);
  if (!ctx) {
    throw new Error('useRigContext must be used within RigContextProvider');
  }
  return ctx;
}

// Optional hook that doesn't throw - for components that may be outside provider
export function useRigContextSafe() {
  return useContext(RigContext);
}
